import React, { useState, useEffect, useRef } from 'react';
import { X, User, Lock, Phone, Save, Loader, Check, Mail, LogOut, ShieldCheck, AlertTriangle } from 'lucide-react';

import { authService, userService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

// Reusable input — same style as PaymentDetailModal
const InputField = ({ label, hint, icon: Icon, disabled, error, ...props }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    <div className="relative group">
      {Icon && (
        <Icon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors duration-200 ${
          disabled ? 'text-gray-300' : (error ? 'text-red-400' : 'text-gray-400 group-focus-within:text-blue-500')
        }`} />
      )}
      <input
        {...props}
        disabled={disabled}
        className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2.5 border rounded-lg text-sm outline-none transition-all duration-200 ${
          disabled
            ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
            : error
              ? 'border-red-300 focus:ring-2 focus:ring-red-100 focus:border-red-500 text-red-600'
              : 'border-gray-200 hover:border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:shadow-sm'
        }`}
      />
    </div>
    {error ? (
      <p className="text-xs text-red-500 mt-1">{error}</p>
    ) : (
      hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>
    )}
  </div>
);

export default function UserSettingsPanel({ currentUser, onClose, onLogout, defaultTab = 'profile', onPasswordChanged }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false); // for button checkmark flash
  const [message, setMessage] = useState(null);
  const [tabKey, setTabKey] = useState(0); // forces re-mount on tab switch for stagger anim

  const [profile, setProfile] = useState({
    name: currentUser?.name || '',
    phone: '',
    email: currentUser?.email || ''
  });

  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });

  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser?.email) return;
      try {
        const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'allowed_users', currentUser.email);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfile(prev => ({ ...prev, name: data.name || prev.name, phone: data.phone || '' }));
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    loadProfile();
  }, [currentUser?.email]);

  const handleSaveProfile = async () => {
    if (!currentUser?.email) return;
    setSaving(true);
    setMessage(null);
    try {
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'allowed_users', currentUser.email);
      await setDoc(docRef, { name: profile.name, phone: profile.phone }, { merge: true });
      if (currentUser?.uid) {
        try {
          const sysUserRef = doc(db, 'artifacts', appId, 'public', 'data', 'system_users', currentUser.uid);
          const sysUserSnap = await getDoc(sysUserRef);
          if (sysUserSnap.exists()) {
            await setDoc(sysUserRef, { name: profile.name, phone: profile.phone }, { merge: true });
          }
        } catch (sysErr) {
          console.warn('Could not update system_users, skipping.', sysErr);
        }

        try {
          await userService.update({
            id: currentUser.uid,
            username: profile.name
          });

          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const userData = JSON.parse(storedUser);
            if (userData.name !== profile.name) {
              userData.name = profile.name;
              localStorage.setItem('user', JSON.stringify(userData));
              setTimeout(() => {
                window.location.reload();
              }, 1500);
            }
          }
        } catch (apiErr) {
          console.error('Could not update user via API:', apiErr);
        }
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      setMessage({ type: 'success', text: t('profileUpdated') || 'Profile updated successfully!' });
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: t('profileUpdateFailed') || 'Failed to save profile. Please try again.' });
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      setMessage({ type: 'error', text: t('fillAllPasswordFields') || 'Please fill in all password fields.' });
      return;
    }
    if (passwords.new !== passwords.confirm) {
      setMessage({ type: 'error', text: t('passwordsDoNotMatch') || 'New passwords do not match.' });
      return;
    }
    if (passwords.new.length < 6) {
      setMessage({ type: 'error', text: t('passwordMinLength') || 'Password must be at least 6 characters.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await authService.changePassword(passwords.current, passwords.new);
      setPasswords({ current: '', new: '', confirm: '' });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      setMessage({ type: 'success', text: t('passwordChanged') || 'Password changed successfully!' });
      onPasswordChanged?.();
    } catch (error) {
      console.error('Error changing password:', error);
      let errorMsg = t('passwordChangeFailed') || 'Failed to change password. Please try again.';
      if (error.message?.includes('auth/wrong-password') || error.message?.includes('INVALID_LOGIN_CREDENTIALS')) {
        errorMsg = t('currentPasswordIncorrect') || 'Current password is incorrect.';
      } else if (error.message?.includes('auth/requires-recent-login')) {
        errorMsg = t('requiresRecentLogin') || 'Please log out and log back in before changing your password.';
      }
      setMessage({ type: 'error', text: errorMsg });
    }
    setSaving(false);
  };

  const handleLogout = async () => {
    try {
      if (onLogout) await onLogout();
      onClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleTabSwitch = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    setMessage(null);
    setTabKey(k => k + 1); // re-trigger stagger animation
  };

  const tabs = [
    { id: 'profile',  label: t('profile')  || 'Profile',  icon: User },
    { id: 'password', label: t('password') || 'Password', icon: Lock }
  ];

  return (
    /* Backdrop — identical to PaymentDetailModal */
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Card — slides up on mobile, scales in on desktop */}
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden
        animate-bounce-in sm:animate-scale-in">

        {/* ── Header ── */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-start justify-between gap-3 sticky top-0 z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {/* Icon badge with subtle pulse ring */}
              <div className="relative">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                {/* online dot */}
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full border border-white" />
              </div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                {t('accountSettings') || 'Account Settings'}
              </h2>
            </div>
            <p className="text-xs text-gray-500 mt-1.5 ml-0.5">{currentUser?.email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 active:scale-90 rounded-full shrink-0 transition-all duration-150"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex border-b border-gray-200 bg-white shrink-0 relative">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabSwitch(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-semibold transition-all duration-200 border-b-2 ${
                activeTab === tab.id
                  ? 'text-blue-600 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <tab.icon className={`w-4 h-4 transition-transform duration-200 ${activeTab === tab.id ? 'scale-110' : ''}`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          <div className="p-4 sm:p-6 space-y-4">

            {/* Alert message — slides down when it appears */}
            {message && (
              <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border animate-slide-down ${
                message.type === 'success'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}>
                {message.type === 'success'
                  ? <Check className="w-4 h-4 shrink-0 animate-pop-in" />
                  : <AlertTriangle className="w-4 h-4 shrink-0 animate-pop-in" />}
                <span className="flex-1">{message.text}</span>
                <button
                  onClick={() => setMessage(null)}
                  className="opacity-60 hover:opacity-100 hover:scale-110 transition-all duration-150"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* ── Profile Tab — stagger children on mount ── */}
            {activeTab === 'profile' && (
              <div key={`profile-${tabKey}`} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 animate-scale-in">
                <div className="stagger-children space-y-4">
                  <InputField
                    label={t('displayName') || 'Display Name'}
                    icon={User}
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder={t('yourName') || 'Your name'}
                  />
                  <InputField
                    label={t('emailAddress') || 'Email Address'}
                    hint={t('emailCannotBeChanged') || 'Email cannot be changed'}
                    icon={Mail}
                    type="email"
                    value={profile.email}
                    disabled
                  />
                  <InputField
                    label={t('phoneNumber') || 'Phone Number'}
                    hint={t('usedForSms') || 'Used for SMS notifications'}
                    icon={Phone}
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder={t('phonePlaceholder') || '03XX-XXXXXXX'}
                  />
                  <div className="flex justify-end pt-1">
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className={`px-5 py-2.5 font-semibold rounded-lg text-sm flex items-center gap-2 transition-all duration-200 active:scale-95 ${
                        saveSuccess
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60'
                      }`}
                    >
                      {saving ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : saveSuccess ? (
                        <Check className="w-4 h-4 animate-pop-in" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {saving
                        ? (t('saving') || 'Saving...')
                        : saveSuccess
                          ? (t('saved') || 'Saved!')
                          : (t('saveProfile') || 'Save Profile')
                      }
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Password Tab — stagger children on mount ── */}
            {activeTab === 'password' && (
              <div key={`password-${tabKey}`} className="animate-scale-in space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                  <div className="stagger-children space-y-4">
                    <InputField
                      label={t('currentPassword') || 'Current Password'}
                      icon={Lock}
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      placeholder={t('enterCurrentPassword') || 'Enter current password'}
                    />
                    <InputField
                      label={t('newPassword') || 'New Password'}
                      icon={Lock}
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                      placeholder={t('enterNewPassword') || 'Enter new password'}
                      maxLength={32}
                      error={passwords.new && passwords.new.length < 6 ? (t('minChar') || 'Minimum 6 characters') : null}
                    />
                    <InputField
                      label={t('confirmNewPassword') || 'Confirm New Password'}
                      icon={Lock}
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      placeholder={t('confirmNewPasswordPlaceholder') || 'Confirm new password'}
                      maxLength={32}
                      error={passwords.confirm && passwords.new !== passwords.confirm ? (t('passwordsDoNotMatch') || 'New passwords do not match') : null}
                    />
                    <div className="flex justify-end pt-1">
                      <button
                        onClick={handleChangePassword}
                        disabled={saving}
                        className={`px-5 py-2.5 font-semibold rounded-lg text-sm flex items-center gap-2 transition-all duration-200 active:scale-95 ${
                          saveSuccess
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60'
                        }`}
                      >
                        {saving ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : saveSuccess ? (
                          <Check className="w-4 h-4 animate-pop-in" />
                        ) : (
                          <ShieldCheck className="w-4 h-4" />
                        )}
                        {saving
                          ? (t('saving') || 'Saving...')
                          : saveSuccess
                            ? (t('saved') || 'Saved!')
                            : (t('changePassword') || 'Change Password')
                        }
                      </button>
                    </div>
                  </div>
                </div>

                {/* Password requirements */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 animate-slide-up" style={{ animationDelay: '0.18s' }}>
                  <p className="text-xs font-semibold text-gray-500 mb-2">
                    {t('passwordRequirements') || 'Password requirements'}
                  </p>
                  <ul className="text-xs text-gray-400 space-y-1.5">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-gray-300 rounded-full shrink-0" />
                      {t('minChar') || 'Minimum 6 characters'}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-gray-300 rounded-full shrink-0" />
                      {t('mixRecommended') || 'Mix of letters and numbers recommended'}
                    </li>
                  </ul>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ── Footer — Sign Out ── */}
        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-white shrink-0">
          <button
            onClick={handleLogout}
            className="w-full py-2.5 text-red-600 hover:bg-red-50 active:scale-95 font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-150 text-sm border border-red-100 hover:border-red-200"
          >
            <LogOut className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
            {t('signOut') || 'Sign Out'}
          </button>
        </div>

      </div>
    </div>
  );
}
